import classNames from 'classnames';
import React, { useMemo, useState, Suspense, useEffect, useRef } from 'react';
import { Button, Empty, Form, Input, InputRef, Skeleton, Switch } from 'antd';
import { ReactComponent as PlusSvg } from '@assets/icons/plus.svg';
import { ReactComponent as UpgradePlanSvg } from '@assets/icons/diamond-upgrade.svg';
import TemplateBlock from './components/TemplateBlock';
import ProcessTabs from './components/ProcessTabs';
import {
  ApprovalWorkflowItemDto,
  AWStageDto,
  AWTemplateDto,
  AWTemplateStageDto
} from '@api/Api';
import { useCurrentWorkspace } from '@hooks/workspace';
import { useAuth } from '@hooks/useAuth';
import { InfiniteScroll } from '@components/InfiniteScroll/InfiniteScroll';
import { useOrganization } from '@components/OrganizationBoundary';
import { useCampaignGetAwDefaultSettings } from '@hooks/queries/campaign';
import { useAWTemplateList } from '@hooks/queries/awTemplate';
import UpgradePlanTooltip from '@components/Tooltip/UpgradePlanTooltip';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import type { StepItemKey } from '@components/Modals/ApprovalModal/ApprovalModal/components/Steps/Steps';
import {
  getCreateAWInitialStage,
  getCreateAWInitialMember,
  getEmptyApprovalWorkflowTemplateList
} from '@components/Modals/ApprovalModal/ApprovalModal/utils';
import type {
  AwModeState,
  AwState,
  AwStateStage
} from '@components/Modals/ApprovalModal/ApprovalModal/ApprovalModal';
import { getAWInitialSettings } from '@components/Modals/ApprovalModal/utils';
import { useCampaignQuery } from '@hooks/queries/campaigns';
import { useAssetQuery } from '@hooks/queries/assets';
import './CreatingApprovalStep.less';

const UpgradeModal = React.lazy(() => import('./components/UpgradeModal'));

export type ActiveTabType = 'useTemplate' | 'create' | 'awSplitCopy';

type CreatingApprovalStepProps = {
  campaignId: string;
  folderId?: string | null;
  awState: AwState | null;
  awMode: AwModeState;
  selectedTemplate: AWTemplateDto | null;
  approvalWorkflowSplit?: ApprovalWorkflowItemDto | undefined;
  setAwMode: (mode: AwModeState) => void;
  setSelectedTemplate: (template: AWTemplateDto | null) => void;
  onChangeStep: (value: StepItemKey) => void;
  setAwState: (value: AwState) => void;
  onCloseModal: () => void;
};

export default function CreatingApprovalStep({
  campaignId,
  folderId,
  awState,
  selectedTemplate,
  awMode,
  approvalWorkflowSplit,
  onCloseModal,
  setSelectedTemplate,
  setAwState,
  onChangeStep,
  setAwMode
}: CreatingApprovalStepProps) {
  const { tourData, goToStep } = useOnboardingProcess();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const { user } = useAuth(true);
  const { currentOrganization } = useOrganization();
  const inputRef = useRef<InputRef>(null);
  const [isPreserveMediaApprovalStatuses, setIsPreserveMediaApprovalStatuses] =
    useState(awState ? !awState?.restart : true);
  const [activeTab, setActiveTab] = useState<ActiveTabType>(() => {
    if (awMode === 'aw-split-copy') return 'awSplitCopy';

    if (
      ['edit-template', 'create-template'].includes(awMode || '') ||
      selectedTemplate
    ) {
      return 'useTemplate';
    }
    return 'create';
  });
  const [onVisibleUpgradeModal, setOnVisibleUpgradeModal] = useState(false);
  const reviewersLimit =
    currentOrganization?.entity?.limitsV2.awStageMembers ??
    Number.POSITIVE_INFINITY;

  const canUseApprovalTemplates =
    !!currentOrganization.entity?.features.approvalTemplates;
  const canManage = !!currentWorkspace?.permissions.manageApprovalTemplates;
  const isShowTemplateList = activeTab === 'useTemplate';

  const { data: defaultSettings, isLoading: isLoadingDefaultSettings } =
    useCampaignGetAwDefaultSettings({
      enabled: awMode !== 'edit',
      params: { campaignId }
    });

  const {
    data: templateList,
    isLoading: isLoadingTemplateList,
    hasNextPage: hasNextPageTemplateList,
    isFetchingNextPage: isFetchingNextPageTemplateList,
    fetchNextPage: fetchNextPageTemplateList
  } = useAWTemplateList({
    enabled: isShowTemplateList && canUseApprovalTemplates,
    workspaceId: currentWorkspace?.id || ''
  });
  const isLoadingTemplate =
    isLoadingTemplateList && isShowTemplateList && canUseApprovalTemplates;

  const approvalWorkflowTemplateList = useMemo(() => {
    if (!canUseApprovalTemplates) {
      return getEmptyApprovalWorkflowTemplateList();
    }
    return templateList?.pages.flatMap((x) => x.edges.map((x) => x.node)) ?? [];
  }, [canUseApprovalTemplates, templateList?.pages]);

  const currentUser = useMemo(
    () => ({
      email: user.email,
      name: user.name,
      pictureUrl: user.pictureUrl,
      me: true
    }),
    [user]
  );

  const hasLimitTemplates = useMemo(() => {
    if (!currentOrganization?.entity?.limitsV2.awTemplates) return false;
    return (
      approvalWorkflowTemplateList.length >=
      currentOrganization?.entity?.limitsV2.awTemplates
    );
  }, [approvalWorkflowTemplateList, currentOrganization]);

  const infiniteScrollLoading = useMemo(() => {
    if (isFetchingNextPageTemplateList) return 'more';
    if (isLoadingTemplate) return 'initial';
    return false;
  }, [isFetchingNextPageTemplateList, isLoadingTemplate]);

  const [approvalRoutingName, setApprovalRoutingName] = useState<string>(
    awState?.name || ''
  );

  const { data: folder } = useAssetQuery(
    {
      assetId: folderId || '',
      isFolder: true
    },
    { enabled: !!folderId }
  );

  const { data: campaign } = useCampaignQuery({
    campaignId
  });

  const awNamePlaceholder =
    selectedTemplate?.name ||
    folder?.folder?.name ||
    campaign?.name ||
    'Enter routing name';

  const isDisabledBtn =
    isShowTemplateList && !selectedTemplate && awMode !== 'edit';

  useEffect(() => {
    if (['edit-template', 'create-template'].includes(awMode || '')) {
      setAwMode('create');
    }
  }, [awMode, setAwMode]);

  useEffect(() => {
    if (!awState?.name) return;
    setApprovalRoutingName(awState.name);
  }, [awState?.name, setApprovalRoutingName]);

  const injectCurrentUserMember = (
    stages: (AWStageDto | AWTemplateStageDto)[]
  ) => {
    return stages.map((stage) => {
      const hasCurrentUser = stage.members.some((member) => member.me);
      if (hasCurrentUser)
        return {
          ...stage,
          isFinished: false
        };
      const newMember = getCreateAWInitialMember(currentUser);
      return {
        ...stage,
        isFinished: false,
        members: [
          {
            ...newMember
          },
          ...stage.members
        ]
      };
    });
  };

  const getAWInitial = async (
    template: AWTemplateDto | null
  ): Promise<AwState> => {
    if (template) {
      return {
        ...template,
        stages: injectCurrentUserMember(template.stages),
        password: template.password ?? null,
        customMessage: template.customMessage ?? null
      };
    }
    const name = approvalRoutingName || awNamePlaceholder;
    if (awMode === 'edit' && awState) {
      return {
        ...awState,
        name
      };
    }

    if (awMode === 'aw-split-copy' && approvalWorkflowSplit) {
      return {
        name,
        stages: injectCurrentUserMember(approvalWorkflowSplit.stages),
        lockDecisions: approvalWorkflowSplit.lockDecisions,
        disableNonApprovedDownloads:
          approvalWorkflowSplit.disableNonApprovedDownloads,
        password: approvalWorkflowSplit.password || null,
        customMessage: approvalWorkflowSplit.customMessage || null,
        allowAllVersions: approvalWorkflowSplit.allowAllVersions,
        allowAllComments: approvalWorkflowSplit.allowAllComments,
        templateId: approvalWorkflowSplit.templateId,
        newVersionBehavior: approvalWorkflowSplit.newVersionBehavior,
        restart: !isPreserveMediaApprovalStatuses
      };
    }
    const defaultStage: AwStateStage = getCreateAWInitialStage(currentUser);
    const settings = getAWInitialSettings(defaultSettings ?? {});
    return {
      ...settings,
      name,
      stages: [defaultStage]
    };
  };

  const goNextStep = async () => {
    if (!approvalRoutingName && !awNamePlaceholder) {
      inputRef.current?.focus();
      return;
    }
    const isUseTemplate =
      canUseApprovalTemplates && selectedTemplate && isShowTemplateList;
    const aWInitial = await getAWInitial(
      isUseTemplate ? selectedTemplate : null
    );
    // const maxMembers = Math.max(
    //   ...aWInitial.stages.map((stage) => stage.members.length)
    // );
    // const reviewersLimitReached = maxMembers >= reviewersLimit;
    // TODO: what to do???
    // console.log(reviewersLimitReached, 'reviewersLimitReached');
    setAwState(aWInitial);
    onChangeStep('media');
  };

  const handleCreateEditTemplate = async (template: AWTemplateDto | null) => {
    if (!canUseApprovalTemplates) return;
    const aWInitial = await getAWInitial(template);
    setAwState(aWInitial);
    setSelectedTemplate(template ?? null);
    setAwMode(template ? 'edit-template' : 'create-template');
    onChangeStep('stages');
  };

  return (
    <div className="approval-step creating-approval-step">
      <Suspense>
        <UpgradeModal
          visible={onVisibleUpgradeModal}
          onCloseUpgradeModal={() => setOnVisibleUpgradeModal(false)}
        />
      </Suspense>
      <div className="approval-step_head">
        <h2 className="approval-step_title">
          {awMode === 'edit' && 'Edit approval routing'}
          {awMode !== 'edit' && 'New approval routing'}
        </h2>
        <div className="approval-step_text">
          {awMode === 'edit' && <>&nbsp;</> /* to keep the same height */}
          {awMode !== 'edit' &&
            'Choose if you would like to create a new approval routing from scratch or use an existing template.'}
        </div>
      </div>
      <div className="approval-step_wrapper">
        <div className="creating-approval-step_inner">
          <Form.Item
            className={classNames(
              'creating-approval-step_name tour-route-for-approval-name',
              {
                'b-outline':
                  tourData?.type === 'route-for-approval' && goToStep === 2
              }
            )}
            label="Approval routing name"
          >
            <Input
              ref={inputRef}
              placeholder={awNamePlaceholder || 'Enter routing name'}
              size="large"
              value={approvalRoutingName}
              onChange={(e) => setApprovalRoutingName(e.target.value)}
            />
          </Form.Item>
          <ProcessTabs
            activeTab={activeTab}
            hasApprovalWorkflowSplit={!!approvalWorkflowSplit}
            disabled={awMode === 'edit'}
            setActiveTab={(value) => {
              setActiveTab(value);
              if (approvalWorkflowSplit) {
                setAwMode(value === 'awSplitCopy' ? 'aw-split-copy' : 'create');
              }
            }}
          />
          {activeTab === 'awSplitCopy' && (
            <div className="creating-approval-step_switch-wrapper">
              <div className="creating-approval-step_switch">
                <Switch
                  checked={isPreserveMediaApprovalStatuses}
                  onChange={setIsPreserveMediaApprovalStatuses}
                />
                <span className="main-body-text main-body-text--semibold">
                  Preserve existing approval decisions
                </span>
              </div>
            </div>
          )}
          {isShowTemplateList && (
            <div
              className={classNames('creating-approval-step_content', {
                'b-disabled': !canUseApprovalTemplates
              })}
            >
              {!canUseApprovalTemplates && (
                <div className="creating-approval-step_upgrade">
                  <Button
                    type="primary"
                    icon={<UpgradePlanSvg />}
                    onClick={() => setOnVisibleUpgradeModal(true)}
                  >
                    Upgrade your plan to use templates
                  </Button>
                </div>
              )}
              <div className="creating-approval-step_content-head">
                <div>
                  <div className="creating-approval-step_content-title">
                    Templates
                  </div>
                  <div className="creating-approval-step_content-text">
                    Create a new approval routing template or select an existing
                    one.
                  </div>
                </div>
                <UpgradePlanTooltip
                  text=" to enterprise plan to unlock additional approval routing templates."
                  isCanVisible={hasLimitTemplates}
                  placement="topRight"
                >
                  <div>
                    <Button
                      className="creating-approval-step_add-btn"
                      disabled={hasLimitTemplates}
                      loading={isLoadingTemplate || isLoadingDefaultSettings}
                      icon={<PlusSvg className="btn-icon" />}
                      onClick={() =>
                        !hasLimitTemplates && handleCreateEditTemplate(null)
                      }
                    >
                      <span className="creating-approval-step_add-btn-text">
                        Add new template
                        {hasLimitTemplates && <UpgradePlanSvg />}
                      </span>
                    </Button>
                  </div>
                </UpgradePlanTooltip>
              </div>

              <div className="creating-approval-step_list">
                <InfiniteScroll
                  loading={infiniteScrollLoading}
                  next={fetchNextPageTemplateList}
                  hasMore={hasNextPageTemplateList || false}
                  loader={
                    <>
                      <Skeleton.Input block size="large" active />
                      <Skeleton.Input block size="large" active />
                    </>
                  }
                >
                  {!approvalWorkflowTemplateList.length &&
                    !isLoadingTemplate && (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="No templates"
                      />
                    )}
                  {!!approvalWorkflowTemplateList.length &&
                    !isLoadingTemplate &&
                    approvalWorkflowTemplateList.map((template) => (
                      <div
                        key={template.id}
                        className="creating-approval-step_block"
                      >
                        <TemplateBlock
                          template={template}
                          selectedTemplate={selectedTemplate}
                          canManage={canManage}
                          setSelectedTemplate={setSelectedTemplate}
                          onEditTemplate={(template) =>
                            handleCreateEditTemplate(template)
                          }
                        />
                      </div>
                    ))}
                </InfiniteScroll>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="approval-step_buttons">
        <Button type="text" onClick={onCloseModal}>
          Cancel
        </Button>
        <Button
          type="primary"
          loading={isLoadingDefaultSettings && awMode !== 'edit'}
          disabled={isDisabledBtn}
          onClick={goNextStep}
          style={{
            minWidth: 152
          }}
        >
          Next step
        </Button>
      </div>
    </div>
  );
}
